import {useTranslation} from 'react-i18next';
import React, {ChangeEvent} from 'react';
import {convertDaysToSeconds} from 'common/dateUtils';
import Panel from 'components/Panel';
import {FormControlLabel, Radio, RadioGroup} from '@mui/material';
import Typography from '@mui/material/Typography';
import TextField from 'components/inputs/TextField';
import DatePicker from 'components/inputs/DatePicker';
import dayjs, {Dayjs} from 'dayjs';
import {useSetFieldValueCb} from 'components/hook-form/useSetFieldValueCb';

enum ExpirationOption {
   Never = 'NEVER',
   In = 'IN',
   On = 'OUT',
}

type ExpirationFieldProps = {
   name: string;
};

const ExpirationField = ({name}: ExpirationFieldProps) => {
   const {t} = useTranslation();
   const [expirationOption, setExpirationOption] = React.useState<ExpirationOption>(
      ExpirationOption.Never
   );
   const [days, setDays] = React.useState(730);
   const [date, setDate] = React.useState(dayjs().add(2, 'years'));
   const setFieldValue = useSetFieldValueCb<any>();

   const onDayChange = (event: ChangeEvent<HTMLInputElement>) => {
      const days = +event.target.value;
      setDays(days);
      setFieldValue(name, convertDaysToSeconds(days));
   };
   const onDateChange = (date: Dayjs | null) => {
      if (date) {
         setDate(date);
         const secs = Math.ceil((date.valueOf() - Date.now()) / 1000);
         setFieldValue(name, secs);
      }
   };
   const onExpirationOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value as ExpirationOption;
      setExpirationOption(value);
      switch (value) {
         case ExpirationOption.Never:
            setFieldValue(name, 0);
            break;
         case ExpirationOption.In:
            setFieldValue(name, convertDaysToSeconds(days));
            break;
         case ExpirationOption.On:
            //TODO: clarify the logic if expiration date is the same day as today
            const secs = Math.ceil((date.valueOf() - Date.now()) / 1000);
            setFieldValue(name, secs);
      }
   };
   const setExpirationOptionOnFocus = (value: ExpirationOption) => {
      if (expirationOption !== value) {
         setExpirationOption(value);
      }
   };
   return (
      <Panel name={t('Expiration')}>
         <RadioGroup value={expirationOption} onChange={onExpirationOptionChange}>
            <FormControlLabel
               value={ExpirationOption.Never}
               control={<Radio />}
               label={<Typography variant={'body2'}>{t('Never Expires')}</Typography>}
               onFocus={() => setExpirationOptionOnFocus(ExpirationOption.Never)}
            />
            <FormControlLabel
               value={ExpirationOption.In}
               control={<Radio />}
               label={
                  <TextField
                     type={'number'}
                     label={t('Expires In (days)')}
                     value={days}
                     inputProps={{min: 1}}
                     disabled={expirationOption !== ExpirationOption.In}
                     onChange={onDayChange}
                  />
               }
            />
            <FormControlLabel
               value={ExpirationOption.On}
               control={<Radio />}
               label={
                  <DatePicker
                     disablePast
                     label={t('Expires On')}
                     disabled={expirationOption !== ExpirationOption.On}
                     value={date}
                     onChange={onDateChange}
                  />
               }
            />
         </RadioGroup>
      </Panel>
   );
};

export default ExpirationField;
